<template>
  <div style="width: 100%;">
    <div v-if="loading" class="col-12" style="margin-top: 20px;">
      <inline-alert-component
        message="Carregando gráfico de colunas (Atendidas × Abandonadas)..."
        type="primary"
      />
    </div>

    <div v-else class="card-charts my-3 px-3 py-3">
      <div v-if="error" class="alert alert-danger" role="alert">{{ error }}</div>

      <div class="sub-card" v-if="!error">
        <h3 style="color:#000; text-align:center">
          <i class="fas fa-chart-bar"></i> Ligações por dia (Atendidas × Abandonadas)
        </h3>
        <hr />
        <apexchart :key="chartKey" type="bar" :options="chartOptions" :series="series" />
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";

export default {
  name: "CombinedBarCallsReport",
  props: {
    // filter: { startDate, endDate, queue }
    filter: { type: Object, required: true },
  },
  data() {
    return {
      loading: false,
      error: "",
      chartKey: 0,
      series: [],
      chartOptions: {
        chart: {
          id: "combinedBarCallsReport",
          type: "bar",
          stacked: true,
          height: 250,
          toolbar: {
            show: true,
            tools: { download: true },
            export: {
              csv: {
                filename: "ligacoes-atendidas-x-abandonadas-colunas",
                headerCategory: "Data",
                headerValue: "Quantidade",
              },
              png: { filename: "ligacoes-atendidas-x-abandonadas-colunas" },
              svg: { filename: "ligacoes-atendidas-x-abandonadas-colunas" },
            },
          },
          locales: [
            {
              name: "pt",
              options: {
                toolbar: {
                  exportToSVG: "Baixar SVG – Colunas (Atendidas × Abandonadas)",
                  exportToPNG: "Baixar PNG – Colunas (Atendidas × Abandonadas)",
                  exportToCSV: "Baixar CSV – Colunas (Atendidas × Abandonadas)",
                  menu: "Menu",
                  selection: "Selecionar",
                  selectionZoom: "Zoom de seleção",
                  zoomIn: "Aproximar",
                  zoomOut: "Afastar",
                  pan: "Arrastar",
                  reset: "Redefinir zoom",
                  download: "Baixar",
                },
              },
            },
          ],
          defaultLocale: "pt",
        },
        plotOptions: { bar: { horizontal: false, dataLabels: { total: { enabled: true } } } },
        xaxis: {
          type: "category",
          categories: [],
          labels: { formatter: (v) => moment(v, "YYYY-MM-DD").format("DD/MM/YY") },
        },
        legend: { position: "top" },
        fill: { opacity: 1 },
        colors: ["#53e713", "#ff0000"], // redundante; cores já travadas por série
        noData: { text: "Sem dados para exibir no momento..." },
        yaxis: { labels: { formatter: (v) => (v | 0).toString() } },
        tooltip: { shared: true, intersect: false },
      },
    };
  },
  methods: {
    // aceita tanto array quanto { name, data: [...] }
    arrData(x) {
      if (Array.isArray(x)) return x;
      if (x && Array.isArray(x.data)) return x.data;
      return [];
    },
    async resolveQueueParam(queue) {
      // Barras não aceitam "*": se vier "*", buscamos todas as filas e montamos "a,b,c"
      if (queue === "*") {
        const { data } = await axios.get("/dashboard/getQueues");
        const names = (Array.isArray(data) ? data : [])
          .map((q) => q?.queuename || q?.name || q) // robusto pra diferentes formatos
          .filter(Boolean);
        return names.join(",");
      }
      if (Array.isArray(queue)) return queue.join(",");
      return queue || "";
    },
    async getData() {
      this.error = "";
      if (!this.filter?.startDate || !this.filter?.endDate) return;
      this.loading = true;

      try {
        const queueParam = await this.resolveQueueParam(this.filter.queue);
        const params = new URLSearchParams({
          startDate: this.filter.startDate,
          endDate: this.filter.endDate,
          queue: queueParam,
        }).toString();

        // rotas ficam sob /dashboard
        const urlAnswered = `/dashboard/answeredCallsBarChartReport?${params}`;
        const urlAbandoned = `/dashboard/abandonedCallsBarChartReport?${params}`;

        const [{ data: ans }, { data: abd }] = await Promise.all([
          axios.get(urlAnswered),
          axios.get(urlAbandoned),
        ]);

        const dates =
          (Array.isArray(ans?.dates) && ans.dates.length ? ans.dates : null) ||
          (Array.isArray(abd?.dates) && abd.dates.length ? abd.dates : []) ||
          [];

        const atendidas = dates.map((_, i) => {
          const mai = this.arrData(ans?.maior1MinEspera)[i] || 0;
          const men = this.arrData(ans?.menor1MinEspera)[i] || 0;
          return +mai + +men;
        });

        const abandonadas = dates.map((_, i) => {
          const mai = this.arrData(abd?.maior1MinEspera)[i] || 0;
          const men = this.arrData(abd?.menor1MinEspera)[i] || 0;
          return +mai + +men;
        });

        // Cores travadas por série (garante mapeamento correto)
        this.series = [
          { name: "Ligações atendidas", data: atendidas, color: "#53e713" },
          { name: "Ligações abandonadas", data: abandonadas, color: "#ff0000" },
        ];

        this.chartOptions = {
          ...this.chartOptions,
          xaxis: { ...this.chartOptions.xaxis, categories: dates },
        };
        this.chartKey++;
      } catch (err) {
        console.error("[CombinedBar] erro:", err);
        this.error =
          "Não consegui carregar as séries. Verifique o intervalo de datas e, se estiver usando '*', mantenha-o (eu já expando para todas as filas).";
      } finally {
        this.loading = false;
      }
    },
  },
  watch: {
    filter: { deep: true, immediate: true, handler() { this.getData(); } },
  },
};
</script>

<style scoped>
.card-charts {
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  width: 100%;
  border-radius: 12px;
  box-shadow: -5px -5px 8px rgba(255,255,255,1),
              -2px -2px 12px rgba(255,255,255,0.5),
               inset 2px 2px 4px rgba(255,255,255,0.1),
               5px 5px 8px rgba(0,0,0,0.15);
}
.sub-card { width: 75%; margin: 0 auto; }
</style>
